import React, { useEffect, useRef } from 'react';
import ExperimentLayout from '../../components/experiments/ExperimentLayout';

const ScrapHeapSprintExperiment: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let unityInstance: unknown = null;

    const loadUnityGame = async () => {
      if (!canvasRef.current || !containerRef.current) return;

      try {
        // Load Unity loader script
        const script = document.createElement('script');
        script.src = '/experiments/scrapheap-sprint/Build/ScrapheapSprint.loader.js';
        script.onload = () => {
          const canvas = canvasRef.current;
          if (!canvas) return;

          const buildUrl = '/experiments/scrapheap-sprint/Build';
          const config = {
            dataUrl: buildUrl + '/ScrapheapSprint.data.br',
            frameworkUrl: buildUrl + '/ScrapheapSprint.framework.js.br',
            codeUrl: buildUrl + '/ScrapheapSprint.wasm.br',
            streamingAssetsUrl: 'StreamingAssets',
            companyName: 'DefaultCompany',
            productName: 'ScrapheapSprint1',
            productVersion: '1.0',
            showBanner: (msg: string, type: string) => {
              const warningBanner = document.querySelector('#unity-warning');
              if (warningBanner) {
                const div = document.createElement('div');
                div.innerHTML = msg;
                warningBanner.appendChild(div);
                if (type === 'error') div.style.cssText = 'background: red; padding: 10px;';
                else {
                  if (type === 'warning') div.style.cssText = 'background: yellow; padding: 10px;';
                  setTimeout(() => {
                    if (div.parentNode) {
                      warningBanner.removeChild(div);
                    }
                  }, 5000);
                }
              }
            },
          };

          // Adjust canvas size for responsive design
          canvas.width = Math.min(1200, window.innerWidth - 40);
          canvas.height = (canvas.width * 600) / 960;

          // Create Unity instance
          (window as { createUnityInstance?: (canvas: HTMLCanvasElement, config: unknown, progressCallback: (progress: number) => void) => Promise<{ Quit: () => void }> }).createUnityInstance?.(canvas, config, (progress: number) => {
            const progressBar = document.querySelector('#unity-progress-bar-full') as HTMLElement;
            if (progressBar) {
              progressBar.style.width = (100 * progress) + '%';
            }
          }).then((instance: { Quit: () => void }) => {
            unityInstance = instance;
            const loadingBar = document.querySelector('#unity-loading-bar') as HTMLElement;
            if (loadingBar) {
              loadingBar.style.display = 'none';
            }
          }).catch((message: string) => {
            console.error('Unity loading failed:', message);
          });
        };

        document.head.appendChild(script);
      } catch (error) {
        console.error('Failed to load Unity game:', error);
      }
    };

    loadUnityGame();

    return () => {
      if (unityInstance && typeof unityInstance === 'object' && 'Quit' in unityInstance) {
        (unityInstance as { Quit: () => void }).Quit();
      }
    };
  }, []);

  return (
    <ExperimentLayout
      title="ScrapHeap Sprint"
      summary="A Unity-powered racing game where you navigate through obstacles and collect power-ups. Built with Unity 6 and deployed as WebGL."
      meta={[
        <span key="unity" className="px-2 py-1 rounded-md bg-purple-500/10 text-purple-200">Unity</span>,
        <span key="webgl" className="px-2 py-1 rounded-md bg-blue-500/10 text-blue-200">WebGL</span>,
        <span key="game" className="px-2 py-1 rounded-md bg-green-500/10 text-green-200">Game</span>,
        <span key="3d" className="px-2 py-1 rounded-md bg-orange-500/10 text-orange-200">3D</span>
      ]}
    >
      <div className="space-y-6">
        <section className="space-y-3">
          <h2 className="text-base font-medium text-white/80">Game Controls</h2>
          <div className="text-xs text-white/50 space-y-1">
            <p>• Your ship is falling apart! Salvage nearby components to navigate the asteroid fields between the oases of parts</p>
            <p>• Left Click and drag components to attach them to your ship!</p>
            <p>• Press the Keys Shown to use attached components. Spacebar to brake</p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-medium text-white/80">Play Game</h2>
          <div
            ref={containerRef}
            className="unity-container bg-gray-900 rounded-lg overflow-hidden border border-white/10"
            style={{ maxWidth: '1200px', margin: '0 auto' }}
          >
            <canvas
              ref={canvasRef}
              id="unity-canvas"
              className="w-full h-auto"
              tabIndex={-1}
            />
            <div id="unity-loading-bar" className="flex flex-col items-center justify-center p-8 space-y-4">
              <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center">
                <div className="w-8 h-8 bg-brand-400 rounded animate-pulse"></div>
              </div>
              <div className="w-64 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div id="unity-progress-bar-full" className="h-full bg-brand-400 transition-all duration-300" style={{ width: '0%' }}></div>
              </div>
              <p className="text-white/60 text-sm">Loading ScrapHeap Sprint...</p>
            </div>
            <div id="unity-warning" className="hidden"></div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-medium text-white/80">Technical Details</h2>
          <div className="text-xs text-white/50 space-y-1">
            <p>• Built with Unity 6 (Editor version 6000.2.6f2) and compiled to WebGL</p>
            <p>• Uses compressed Brotli assets for faster loading</p>
            <p>• Responsive canvas that adapts to screen size</p>
            <p>• Optimized for web performance</p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-medium text-white/80">Project & Credits</h2>
          <div className="text-xs text-white/50 space-y-2">
            <p>
              <a
                href="https://github.com/sketchycode/ScrapheapSprint"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-300 hover:text-brand-200 underline transition-colors"
              >
                View Source Code on GitHub →
              </a>
            </p>
            <div>
              <p className="text-white/60 font-medium mb-1">Development Team:</p>
              <ul className="space-y-1 ml-2">
                <li>• Christopher Bachicha (Game Developer)</li>
                <li>• <a href="https://github.com/sketchycode" target="_blank" rel="noopener noreferrer" className="text-brand-300 hover:text-brand-200 underline transition-colors">Jeremiah Nunn</a> (Game Developer)</li>
                <li>• <a href="https://github.com/vipaka" target="_blank" rel="noopener noreferrer" className="text-brand-300 hover:text-brand-200 underline transition-colors">Amelia Winstead</a> (Sound, Music, & Art)</li>
                <li>• <a href="https://github.com/tpendergrass" target="_blank" rel="noopener noreferrer" className="text-brand-300 hover:text-brand-200 underline transition-colors">Thomas Pendergrass</a> (3D Models, Game Developer)</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </ExperimentLayout>
  );
};

export default ScrapHeapSprintExperiment;
